import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';

import type { OpcionPantalla } from './opciones';

// Las pantallas la importan desde aquí (los `.ts` sueltos no se resuelven
// con `#components/...`).
export { opcionAnadirMovimiento } from './opciones';

/**
 * Registro de las opciones de la pantalla activa (la brújula de la barra).
 * Cada parte de una pantalla (la página, su tabla, su cabecera…) llama a
 * `useOpcionesPantalla([...])`; la barra junta todo lo registrado y lo
 * ordena con `ordenarOpciones`. Al desmontarse, cada parte se borra sola.
 */
type ApiRegistro = {
  registrar: (clave: string, opciones: OpcionPantalla[]) => void;
  quitar: (clave: string) => void;
};

const RegistroContext = createContext<ApiRegistro | null>(null);
const ListaContext = createContext<OpcionPantalla[]>([]);

export function ProveedorOpciones({ children }: { children: ReactNode }) {
  const [registros, setRegistros] = useState<
    ReadonlyMap<string, OpcionPantalla[]>
  >(() => new Map());

  const registrar = useCallback((clave: string, opciones: OpcionPantalla[]) => {
    setRegistros(previos => {
      const nuevos = new Map(previos);
      nuevos.set(clave, opciones);
      return nuevos;
    });
  }, []);
  const quitar = useCallback((clave: string) => {
    setRegistros(previos => {
      if (!previos.has(clave)) {
        return previos;
      }
      const nuevos = new Map(previos);
      nuevos.delete(clave);
      return nuevos;
    });
  }, []);

  const api = useMemo(() => ({ registrar, quitar }), [registrar, quitar]);
  const lista = useMemo(() => [...registros.values()].flat(), [registros]);

  return (
    <RegistroContext.Provider value={api}>
      <ListaContext.Provider value={lista}>{children}</ListaContext.Provider>
    </RegistroContext.Provider>
  );
}

/** Lo que la brújula enseña: todas las opciones registradas, sin ordenar. */
export function useOpcionesRegistradas(): OpcionPantalla[] {
  return useContext(ListaContext);
}

/** Lo que cambia lo que se ve de una opción (no su `onPress`). */
function firmaDe(opciones: readonly OpcionPantalla[]): string {
  return opciones
    .map(o =>
      [
        o.id,
        o.tipo,
        o.texto,
        o.icono,
        o.oculta ? 1 : 0,
        o.desactivada ? 1 : 0,
        o.prioridad ?? 0,
      ]
        .map(String)
        .join('\u0001'),
    )
    .join('\u0002');
}

/**
 * Declara las opciones de esta parte de la pantalla para la brújula. Se
 * puede llamar en cada render con funciones nuevas: solo se vuelve a
 * registrar si cambia lo visible (texto, icono, oculta…), y `onPress` llama
 * siempre a la versión más reciente. Sin `ProveedorOpciones` (tests de una
 * página suelta) no hace nada.
 */
export function useOpcionesPantalla(opciones: OpcionPantalla[]) {
  const api = useContext(RegistroContext);
  const clave = useId();
  const ultimas = useRef(opciones);
  useLayoutEffect(() => {
    ultimas.current = opciones;
  });
  const firma = firmaDe(opciones);

  useEffect(() => {
    if (!api) {
      return;
    }
    api.registrar(
      clave,
      ultimas.current.map(o => ({
        ...o,
        onPress: () => ultimas.current.find(x => x.id === o.id)?.onPress(),
      })),
    );
  }, [api, clave, firma]);

  useEffect(() => {
    if (!api) {
      return;
    }
    return () => api.quitar(clave);
  }, [api, clave]);
}
